import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import EmberField from "@/components/EmberField";
import { CinematicBackdrop } from "@/components/CinematicBackdrop";
import { HERO } from "@/constants/testIds";
import { HeroVoice } from "@/components/HeroVoice";
import AudioListen from "@/components/AudioListen";

const line1 = ["Through", "Adversity"];
const line2 = ["Comes", "Change."];

const wordVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.15, ease: [0.65, 0, 0.35, 1], delay: 0.4 + i * 0.09 },
  }),
};

export default function Hero() {
  return (
    <section
      data-testid={HERO.section}
      className="relative min-h-[100svh] w-full overflow-hidden pt-32 md:pt-40 pb-24"
    >
      <CinematicBackdrop />
      <EmberField />

      {/* Vertical rule + section tag */}
      <div className="absolute top-0 bottom-0 left-6 md:left-10 w-px bg-black/5" aria-hidden />
      <div className="hidden md:block absolute top-32 md:top-40 left-6 md:left-10 -translate-x-1/2 rotate-180 [writing-mode:vertical-rl] font-mono text-[10px] tracking-[0.4em] text-[#78716C] uppercase">
        A Manifesto · 01 / 08
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-y-10">
        {/* Left column: overline + subtext */}
        <div className="col-span-12 lg:col-span-4 flex flex-col justify-between order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
            className="space-y-6"
          >
            <div className="section-tag">A Faith-Led Healing Journey · Est. 2025</div>
            <p className="text-[#44403C] text-base md:text-lg leading-relaxed max-w-md">
              This isn&apos;t a normal course — it&apos;s a real healing experience. Across eight modules, Rich walks with you to stop running, face your past, and finally heal. God has been with you the whole time — because God is love.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Link to="/modules" data-testid={HERO.ctaModules} className="btn-ember text-sm">
                Explore the 8 Modules
                <ArrowUpRight size={16} strokeWidth={1.6} />
              </Link>
              <Link to="/b2b" data-testid={HERO.ctaB2b} className="btn-outline text-sm">
                For Companies
              </Link>
              <AudioListen
                text="Hey. I'm Rich. If you're carrying something heavy right now, you're in the right place. This whole thing was built on my worst mornings, and it held. Through adversity comes change. Let me walk you through it."
                label="Hear Rich · 20 sec"
                variant="block"
                testid="hero-hear-rich"
                speakerLabel="Rich · welcome"
                speakerAvatar="/author/avatar.jpg"
              />
            </div>
            <HeroVoice />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.2, duration: 1 }}
            className="hidden lg:flex items-end gap-6 pt-16"
          >
            <div>
              <div className="font-display text-5xl text-[#B45309] leading-none">08</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mt-2">Healing Modules</div>
            </div>
            <div>
              <div className="font-display text-5xl text-[#1C1917] leading-none">03</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mt-2">Books · Kindle</div>
            </div>
            <div>
              <div className="font-display text-5xl text-[#1C1917] leading-none">∞</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mt-2">Lives Changed</div>
            </div>
          </motion.div>
        </div>

        {/* Right column: massive headline */}
        <div className="col-span-12 lg:col-span-8 order-1 lg:order-2">
          <motion.h1
            data-testid={HERO.headline}
            className="font-display leading-[0.88] tracking-tight text-[16vw] md:text-[13vw] lg:text-[11vw] xl:text-[10.5vw]"
            initial="hidden"
            animate="visible"
          >
            <span className="mask-line">
              {line1.map((w, i) => (
                <motion.span key={w} custom={i} variants={wordVariant} className={i === 1 ? "italic text-[#B45309] pr-3" : "pr-3"}>
                  {w}{" "}
                </motion.span>
              ))}
            </span>
            <span className="mask-line">
              {line2.map((w, i) => (
                <motion.span key={w} custom={i + line1.length} variants={wordVariant} className="pr-3">
                  {w}{" "}
                </motion.span>
              ))}
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.4, duration: 1 }}
            className="flex items-center gap-4 mt-8 lg:mt-12 font-mono text-[11px] tracking-[0.3em] uppercase text-[#78716C]"
          >
            <span className="w-10 h-px bg-[#F59E0B]" />
            God is love · Go through it
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#FAF7F2]" />
    </section>
  );
}
